'use client';

import { useEffect, useState } from 'react';

interface CopyButtonProps {
  value: string;
  label?: string;
  className?: string;
  size?: 'sm' | 'md';
}

/**
 * Copy-to-clipboard pill. Shows a clipboard glyph; flashes "Copied!" for 1.5s
 * after a successful copy. Falls back to a temporary textarea + execCommand
 * when navigator.clipboard isn't available (rare on modern browsers but the
 * extra few lines are cheap insurance).
 */
export function CopyButton({ value, label = 'Copy', className, size = 'sm' }: CopyButtonProps) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return;
    const id = window.setTimeout(() => setCopied(false), 1500);
    return () => window.clearTimeout(id);
  }, [copied]);

  async function copy() {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(value);
      } else {
        const ta = document.createElement('textarea');
        ta.value = value;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      setCopied(true);
    } catch {
      // surface the failure subtly — the button just doesn't flash
    }
  }

  const sizeClass = size === 'sm' ? 'h-6 px-1.5 text-[10px]' : 'h-7 px-2 text-xs';

  return (
    <button
      type="button"
      onClick={copy}
      title={copied ? 'Copied!' : `${label}: ${value}`}
      aria-label={copied ? `${value} copied to clipboard` : `${label} ${value}`}
      className={`inline-flex items-center gap-1 rounded border border-neutral-300 bg-white font-medium text-neutral-700 transition-colors hover:bg-neutral-50 active:bg-neutral-100 ${sizeClass} ${className ?? ''}`}
    >
      {copied ? (
        <>
          <CheckIcon />
          <span>Copied</span>
        </>
      ) : (
        <>
          <ClipboardIcon />
          <span>{label}</span>
        </>
      )}
    </button>
  );
}

function ClipboardIcon() {
  return (
    <svg viewBox="0 0 16 16" width="11" height="11" aria-hidden="true" fill="currentColor">
      <path d="M5 2a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v1h1.5A1.5 1.5 0 0 1 14 4.5v9A1.5 1.5 0 0 1 12.5 15h-9A1.5 1.5 0 0 1 2 13.5v-9A1.5 1.5 0 0 1 3.5 3H5V2zm1 .5V3h4v-.5h-4zM3.5 4a.5.5 0 0 0-.5.5v9a.5.5 0 0 0 .5.5h9a.5.5 0 0 0 .5-.5v-9a.5.5 0 0 0-.5-.5H11v.5a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V4H3.5z" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg viewBox="0 0 16 16" width="11" height="11" aria-hidden="true" fill="currentColor">
      <path d="M13.78 4.22a.75.75 0 0 1 0 1.06l-7.25 7.25a.75.75 0 0 1-1.06 0L2.22 9.28a.75.75 0 0 1 1.06-1.06L6 10.94l6.72-6.72a.75.75 0 0 1 1.06 0z" />
    </svg>
  );
}
